import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TID } from "@/constants/testIds";

const ROOMS = [
  {
    id: "olive",
    name: "Dark Olive Lounge",
    image: "https://images.unsplash.com/photo-1615873968403-89e068629265?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    wallColor: "#3F4632",
  },
  {
    id: "loft",
    name: "Minimalist White Loft",
    image: "https://images.unsplash.com/photo-1600210491369-e753d80a41f3?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    wallColor: "#F2EEE6",
  },
  {
    id: "leather",
    name: "Warm Leather Gallery",
    image: "https://images.unsplash.com/photo-1615876234886-fd9a39fda97f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    wallColor: "#2C221B",
  },
];

const FRAMES = [
  { id: "walnut", name: "Walnut", color: "#4E3521", width: 14 },
  { id: "matt-black", name: "Matte Black", color: "#0F0F0E", width: 12 },
  { id: "gallery-white", name: "Gallery White", color: "#F5F3EB", width: 12 },
  { id: "brass", name: "Antique Brass", color: "#8C6F3B", width: 10 },
];

export default function RoomVisualizer({ open, onOpenChange, artwork }) {
  const [roomId, setRoomId] = useState(ROOMS[0].id);
  const [frameId, setFrameId] = useState(FRAMES[0].id);

  const room = ROOMS.find((r) => r.id === roomId);
  const frame = FRAMES.find((f) => f.id === frameId);

  if (!artwork) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] p-0 overflow-hidden bg-[hsl(var(--bg))] border border-[hsl(var(--line))]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
          <div className="relative">
            <DialogTitle className="sr-only">
              See {artwork.title} in your home
            </DialogTitle>
            <DialogDescription className="sr-only">
              Preview {artwork.title} by Colin Darke on a wall in your home,
              with your choice of frame style and room setting.
            </DialogDescription>
            <div
              className="relative w-full"
              style={{
                aspectRatio: "16/10",
                backgroundImage: `url(${room.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* wall tint overlay */}
              <div
                className="absolute inset-0 mix-blend-multiply"
                style={{ background: room.wallColor, opacity: 0.05 }}
              />
              {/* framed artwork */}
              <div
                className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 shadow-2xl bg-[hsl(var(--bg))] flex items-center justify-center"
                style={{
                  width: "34%",
                  padding: frame.width,
                  background: frame.color,
                  boxShadow:
                    "0 30px 60px -20px rgba(0,0,0,0.55), 0 8px 20px rgba(0,0,0,0.25)",
                }}
              >
                <div
                  className="w-full bg-[hsl(var(--bg))] flex items-center justify-center overflow-hidden"
                  style={{ aspectRatio: "4/5" }}
                >
                  <img
                    src={artwork.image_url}
                    alt={artwork.title}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          <aside className="p-6 sm:p-8 border-t lg:border-t-0 lg:border-l border-[hsl(var(--line))] bg-[hsl(var(--surface-elevated))]">
            <p className="catalog-label">See it in your home</p>
            <h3 className="font-serif text-2xl mt-1">{artwork.title}</h3>
            <p className="text-sm text-[hsl(var(--ink-muted))] mt-1">
              {artwork.medium} · {artwork.dimensions}
            </p>

            <div className="mt-8">
              <p className="catalog-label mb-3">Room</p>
              <div className="grid grid-cols-3 gap-2" data-testid={TID.roomSelector}>
                {ROOMS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setRoomId(r.id)}
                    data-testid={`room-${r.id}`}
                    className={`aspect-[4/3] overflow-hidden border transition-all ${
                      roomId === r.id
                        ? "border-[hsl(var(--vermillion))] ring-1 ring-[hsl(var(--vermillion))]"
                        : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
                    }`}
                    title={r.name}
                  >
                    <img
                      src={r.image}
                      alt={r.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="catalog-label mb-3">Frame</p>
              <div className="grid grid-cols-2 gap-2">
                {FRAMES.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFrameId(f.id)}
                    data-testid={`frame-${f.id}`}
                    className={`flex items-center gap-2 px-3 py-2 text-sm border transition-all ${
                      frameId === f.id
                        ? "border-[hsl(var(--vermillion))] bg-[hsl(var(--bg))]"
                        : "border-[hsl(var(--line))] bg-[hsl(var(--bg))] hover:border-[hsl(var(--ink))]"
                    }`}
                  >
                    <span
                      className="w-4 h-4 border border-[hsl(var(--line))]"
                      style={{ background: f.color }}
                    />
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 text-sm text-[hsl(var(--ink-muted))] leading-relaxed">
              Preview only — dimensions may vary by wall size. For a scaled
              mock-up of your own space, send a photo through the inquiry form.
            </div>

            <a
              href="/contact"
              className="mt-6 inline-block w-full text-center bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-mono uppercase text-xs tracking-widest py-3 hover:bg-[hsl(var(--vermillion))] transition-colors"
              data-testid="inquire-room-btn"
            >
              Inquire about this piece
            </a>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
